import { Component, ElementRef, Input, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { TemplateUtilService } from './template-util.service';

/**
 * Displays loading overlay, which will cover its container parent with a translucent background.
 *
 * @example ```
 * <tcc-loadingOverlay *ngIf="state == 'loading'"></tcc-loadingOverlay>
 * <tcc-loadingOverlay *ngIf="state == 'loading'" text="Replace Loading Text"></tcc-loadingOverlay>
 * <tcc-loadingOverlay *ngIf="state == 'loading'">Custom Content</tcc-loadingOverlay>
 * ```
 */
@Component({
    selector: 'tcc-loadingOverlay-signatures',
    template: `
<div class="back" #Back></div>
<div class="fore" #Fore>
  <div class="text-center" #ContentContainer><ng-content></ng-content></div>
  <span>{{getSimpleText()}} <i class="fa fa-spinner fa-pulse"></i></span>
</div>
`,
    styles: [`
    .back { background-color: rgb(255,255,255); top:0; right:0; opacity: .75; }
    .fore {
      text-align:center;
      font-weight:bold;
      width: 100%;
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
    }
    .back, .fore { position: absolute; z-index: 99999; left:0; }
  `],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class LoadingOverlayComponent implements OnDestroy, OnInit {

  /** Background element ref */
  @ViewChild('Back', { static: true }) backElemRef!: ElementRef;

  /** Contains the ng-content element if included */
  @ViewChild('ContentContainer', { static: true }) contentContainerRef!: ElementRef;

  /** Forground element ref */
  @ViewChild('Fore', { static: true }) foreElemRef!: ElementRef;

  /** Optional Input string to replace standard loading text. */
  @Input() text?: string;

  /**
   * While enabled the overlay will continue to check to see if it needs to resize itself.
   * This value is result of setInterval
   */
  private intervalId?: any;
  constructor(private elementRef: ElementRef, private templateUtil: TemplateUtilService) {

  }

  ngOnInit() {
    const componentElem = this.elementRef.nativeElement as HTMLElement;
    const backElem = this.backElemRef.nativeElement as HTMLElement;
    const foreElem = this.foreElemRef.nativeElement as HTMLElement;
    let parentElem: Element | undefined | null;

    let curDim = { x: 0, y: 0, top: 0, clientHeight: 0 };

    doUpdate();
    this.intervalId = setInterval(doUpdate, 50);

    function doUpdate() {
      if (!parentElem) {
        parentElem = componentElem.offsetParent;
        if (!parentElem) {
          return;
        }
      }
      const newDim = {
        x: parentElem.scrollWidth,
        y: parentElem.scrollHeight,
        top: parentElem.scrollTop,
        clientHeight: parentElem.clientHeight
      };

      if (newDim.y !== curDim.y || newDim.top !== curDim.top
        || newDim.clientHeight !== curDim.clientHeight) {

        curDim = newDim;
        backElem.style.height = curDim.y + 'px';
        foreElem.style.top = (curDim.top + (curDim.clientHeight / 2) - (foreElem.clientHeight / 2)) + 'px';
      }
    }
  }

  ngOnDestroy() {
    clearInterval(this.intervalId);
  }

  /**
   * Returns simple text content that is displayed before the loading spinner
   */
  getSimpleText() {
    return (this.templateUtil.elemIsEmpty(this.contentContainerRef)) ? this.text ?? 'Loading' : undefined;
  }
}
